// bluaIQ — Legacy brand localStorage shim
//
// Background:
//   bluaIQ was originally launched as "ConstructAI". Some user
//   preferences are still persisted under `constructai_*` keys in
//   browser localStorage. We are migrating to `bluaiq_*` keys.
//
// Behavior:
//   - read(newKey, legacyKey):
//       1) prefer the new key;
//       2) if missing, fall back to the legacy key;
//       3) if a legacy value is found, transparently copy it to the
//          new key so subsequent reads come from the canonical place.
//   - write(newKey, legacyKey, value):
//       writes the new key. ALSO writes the legacy key during the
//       migration window so that older builds still see the value
//       if they happen to load. After the migration window ends,
//       call `clearLegacyKey()` to remove the legacy artifact.
//
// SAFETY:
//   - Existing user preferences are never lost.
//   - Storage failures (Safari private mode, quota errors, SSR) are
//     swallowed; reads return `null`, writes are best-effort.
//   - This module performs no other localStorage refactor and does
//     not touch keys other than the explicit ones the caller passes.
//
// GENERALISATION:
//   - No project, jurisdiction, or product values are hardcoded here.
//   - The shim only knows about the `bluaiq_*` ⇄ `constructai_*`
//     name pair, nothing else.

const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

function safeGet(key: string): string | null {
  if (!isBrowser) return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string): void {
  if (!isBrowser) return
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* quota / private-mode / SSR — best-effort */
  }
}

function safeRemove(key: string): void {
  if (!isBrowser) return
  try {
    window.localStorage.removeItem(key)
  } catch {
    /* best-effort */
  }
}

/**
 * Read a localStorage value, preferring the new bluaiq_* key and
 * transparently migrating any legacy constructai_* value found.
 *
 * Returns `null` if neither key is present (or if storage is
 * unavailable, e.g. SSR / private mode).
 */
export function readLegacyMigratedItem(newKey: string, legacyKey: string): string | null {
  const fresh = safeGet(newKey)
  if (fresh != null) return fresh

  const legacy = safeGet(legacyKey)
  if (legacy == null) return null

  // Transparent migration: copy legacy → new so future reads are
  // served from the canonical key without rerunning this fallback.
  safeSet(newKey, legacy)
  return legacy
}

/**
 * Write a localStorage value, persisting under the new bluaiq_*
 * key and ALSO mirroring to the legacy constructai_* key for the
 * duration of the migration window. After the migration window
 * closes, switch callers to `writeNewItemOnly()` and call
 * `clearLegacyKey()` once to clean up.
 */
export function writeLegacyMigratedItem(newKey: string, legacyKey: string, value: string): void {
  safeSet(newKey, value)
  // Mirror to the legacy key during the migration window so older
  // builds that may load alongside the new build keep reading the
  // user's current preference.
  safeSet(legacyKey, value)
}

/**
 * Post-migration helper. Removes the legacy key once the migration
 * window has closed. Safe to call repeatedly; no-op if the key is
 * absent or storage is unavailable.
 */
export function clearLegacyKey(legacyKey: string): void {
  safeRemove(legacyKey)
}
